import { sql, relations } from 'drizzle-orm';
import {
  index,
  jsonb,
  pgTable,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uniqueIndex,
  customType,
} from 'drizzle-orm/pg-core';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod';

// Postgres full-text search vector — maintained in the DB, declared here so the
// column is part of the schema and is never dropped by a drizzle push.
const tsvector = customType<{ data: string }>({
  dataType() {
    return 'tsvector';
  },
});

// Session storage table (required for authentication)
export const sessions = pgTable(
  'sessions',
  {
    sid: varchar('sid').primaryKey(),
    sess: jsonb('sess').notNull(),
    expire: timestamp('expire').notNull(),
  },
  table => [index('IDX_session_expire').on(table.expire)]
);

// User storage table (required for authentication)
export const users = pgTable(
  'users',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    email: varchar('email').unique().notNull(),
    firstName: varchar('first_name').notNull(),
    lastName: varchar('last_name').notNull(),
    phoneNumber: varchar('phone_number'),
    // Structured address fields
    streetAddress: varchar('street_address'), // e.g., "123 Main St"
    addressLine2: varchar('address_line_2'), // e.g., "Apt 4B" (optional)
    city: varchar('city'), // e.g., "Indianapolis"
    state: varchar('state'), // e.g., "IN"
    zipCode: varchar('zip_code'), // e.g., "46201"
    country: varchar('country').default('US').notNull(), // Default to US
    // Legacy field - will be deprecated but kept for migration
    homeAddress: text('home_address'),
    profileImageUrl: varchar('profile_image_url'),
    password: varchar('password'),
    stripeCustomerId: varchar('stripe_customer_id'),
    stripeSubscriptionId: varchar('stripe_subscription_id'),
    role: varchar('role').default('user').notNull(), // user, creator, admin
    isActive: boolean('is_active').default(true).notNull(),
    // Geography preference — set during onboarding
    preferredRegionId: varchar('preferred_region_id').references(() => regions.id),
    preferredLocalityId: varchar('preferred_locality_id').references(() => localities.id),
    // New user lifecycle + profile fields
    hasCompletedOnboarding: boolean('has_completed_onboarding').default(false).notNull(),
    isEmailVerified: boolean('is_email_verified').default(false).notNull(),
    timezone: varchar('timezone').default('America/New_York').notNull(),
    emailEnrollmentConfirmation: boolean('notify_enrollment_confirmations').default(true).notNull(),
    emailClassReminders: boolean('notify_class_reminders').default(true).notNull(),
    emailCreatorUpdates: boolean('notify_creator_updates').default(true).notNull(),
    emailAccountNotifications: boolean('email_account_notifications').default(true).notNull(),
    emailCalendarReminders: boolean('email_calendar_reminders').default(true).notNull(),
    isBlogWriter: boolean('is_blog_writer').default(false).notNull(),
    // Additive capability: grants ambassador (referral payout) access on top of the
    // primary role, so an existing creator/venue can also be an ambassador.
    isAmbassador: boolean('is_ambassador').default(false).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
    deletedAt: timestamp('deleted_at'), // set on account deletion request
    anonymizedAt: timestamp('anonymized_at'), // set after PII is scrubbed
  },
  table => [
    index('idx_users_email').on(table.email),
    index('idx_users_role').on(table.role),
    index('idx_users_created_at').on(table.createdAt),
  ]
);

// Password reset tokens
export const passwordResetTokens = pgTable('password_reset_tokens', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar('user_id')
    .references(() => users.id)
    .notNull(),
  token: varchar('token').notNull().unique(),
  expiresAt: timestamp('expires_at').notNull(),
  isUsed: boolean('is_used').default(false).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

export const emailVerificationTokens = pgTable(
  'email_verification_tokens',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    tokenHash: varchar('token_hash').notNull().unique(),
    expiresAt: timestamp('expires_at').notNull(),
    usedAt: timestamp('used_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_email_verification_tokens_user_id').on(table.userId),
    index('idx_email_verification_tokens_token_hash').on(table.tokenHash),
    index('idx_email_verification_tokens_expires_at').on(table.expiresAt),
  ]
);

export const outboundEmails = pgTable(
  'outbound_emails',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    recipientUserId: varchar('recipient_user_id').references(() => users.id),
    idempotencyKey: varchar('idempotency_key').notNull(),
    recipientEmail: varchar('recipient_email').notNull(),
    templateKey: varchar('template_key').notNull(),
    templateData: jsonb('template_data')
      .$type<Record<string, unknown>>()
      .default(sql`'{}'::jsonb`)
      .notNull(),
    status: varchar('status').default('queued').notNull(), // queued | processing | sent | failed | dead_lettered | dismissed (an admin acknowledged a dead-lettered email) | skipped
    preferenceKey: varchar('preference_key'),
    bypassPreference: boolean('bypass_preference').default(false).notNull(),
    provider: varchar('provider'),
    providerMessageId: varchar('provider_message_id'),
    sendAttempts: integer('send_attempts').default(0).notNull(),
    lastError: text('last_error'),
    queuedAt: timestamp('queued_at').defaultNow().notNull(),
    lastAttemptAt: timestamp('last_attempt_at'),
    sentAt: timestamp('sent_at'),
    nextRetryAt: timestamp('next_retry_at'),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('uidx_outbound_emails_idempotency_key').on(table.idempotencyKey),
    index('idx_outbound_emails_recipient_user_id').on(table.recipientUserId),
    index('idx_outbound_emails_status').on(table.status),
    index('idx_outbound_emails_template_key').on(table.templateKey),
    index('idx_outbound_emails_next_retry_at').on(table.nextRetryAt),
  ]
);

export const emailUsageTelemetry = pgTable(
  'email_usage_telemetry',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    outboundEmailId: varchar('outbound_email_id').references(() => outboundEmails.id),
    provider: varchar('provider').notNull(),
    templateKey: varchar('template_key').notNull(),
    recipientCount: integer('recipient_count').notNull(),
    externalRecipientCount: integer('external_recipient_count').notNull(),
    status: varchar('status').notNull(),
    errorMessage: text('error_message'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_email_usage_telemetry_provider').on(table.provider),
    index('idx_email_usage_telemetry_template_key').on(table.templateKey),
    index('idx_email_usage_telemetry_created_at').on(table.createdAt),
  ]
);

// US states — one row per state; enables multi-state geography in future phases
export const states = pgTable(
  'states',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    name: varchar('name').notNull(),
    slug: varchar('slug').notNull(),
    abbreviation: varchar('abbreviation', { length: 2 }).notNull(),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('uidx_states_slug').on(table.slug),
    uniqueIndex('uidx_states_abbreviation').on(table.abbreviation),
    index('idx_states_active').on(table.isActive),
  ]
);

// Geography: regions (e.g. Monroe County, Online) and localities (e.g. Bloomington, Ellettsville)
export const regions = pgTable(
  'regions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    name: varchar('name').notNull(),
    slug: varchar('slug').notNull().unique(),
    type: varchar('type').notNull(), // physical | online
    stateId: varchar('state_id').references(() => states.id), // null for Online (global)
    description: varchar('description'), // optional display copy
    timezone: varchar('timezone').notNull().default('UTC'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
  },
  table => [
    index('idx_regions_slug').on(table.slug),
    index('idx_regions_active').on(table.isActive),
    index('idx_regions_state_id').on(table.stateId),
  ]
);

export const localities = pgTable(
  'localities',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    name: varchar('name').notNull(),
    slug: varchar('slug').notNull(),
    regionId: varchar('region_id').references(() => regions.id),
    // Null = top-level locality in the region; set to parent locality ID for sub-localities.
    // e.g. Bloomington.parentLocalityId = Monroe County locality ID
    parentLocalityId: varchar('parent_locality_id'),
    timezone: varchar('timezone').notNull().default('America/Indiana/Indianapolis'),
    isActive: boolean('is_active').notNull().default(true),
    description: text('description'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
  },
  table => [
    index('idx_localities_region_id').on(table.regionId),
    index('idx_localities_slug').on(table.slug),
    index('idx_localities_active').on(table.isActive),
    index('idx_localities_parent').on(table.parentLocalityId),
    uniqueIndex('uidx_localities_region_slug').on(table.regionId, table.slug),
  ]
);

// Named physical venues where classes are held (e.g. "Fig Tree Market")
export const classLocations = pgTable(
  'class_locations',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    localityId: varchar('locality_id').references(() => localities.id),
    // Venue owner (a user with role=venue) who receives this venue's payout share.
    ownerUserId: varchar('owner_user_id').references(() => users.id),
    // 'connect' = pay the venue owner's share to their own Stripe Connect account
    // (requires the owner to complete onboarding). 'platform' = fold the venue's
    // share into Kalovate's cut instead — no separate venue transfer is attempted,
    // regardless of the fee profile's venue %. Set per-venue by an admin for venues
    // that don't want/need a separate connected account.
    payoutMode: varchar('payout_mode').notNull().default('connect'), // connect | platform
    name: varchar('name').notNull(),
    slug: varchar('slug').notNull(),
    address: varchar('address'),
    city: varchar('city'),
    state: varchar('state'),
    zip: varchar('zip'),
    description: text('description'),
    website: varchar('website'),
    phoneNumber: varchar('phone_number'),
    email: varchar('email'),
    hours: text('hours'), // venue's own opening hours, freeform (e.g. "Tue-Sat 10am-6pm")
    thumbnailUrl: varchar('thumbnail_url'),
    socialLinks: jsonb('social_links').$type<{ instagram?: string; facebook?: string }>(),
    isActive: boolean('is_active').notNull().default(true),
    // Sample/demo venue — shown only in its own locality view, hidden from
    // region-wide "All" browse.
    isExample: boolean('is_example').notNull().default(false),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_class_locations_locality_id').on(table.localityId),
    index('idx_class_locations_active').on(table.isActive),
    uniqueIndex('uidx_class_locations_locality_slug').on(table.localityId, table.slug),
  ]
);

// Creator profiles
export const creators = pgTable(
  'creators',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    displayName: varchar('display_name').notNull(),
    bio: text('bio'),
    creatorHeadline: varchar('specialty'),
    experience: text('experience'),
    portfolioUrl: varchar('portfolio_url'),
    profileImageUrl: varchar('profile_image_url'),
    socialLinks: jsonb('social_links'),
    isVerified: boolean('is_verified').default(false).notNull(),
    // Manually curated list for homepage "Featured Creators"
    isFeatured: boolean('is_featured').default(false).notNull(),
    // Partnership badge — displayed as a trust indicator; does NOT gate discovery
    isPartner: boolean('is_partner').default(false).notNull(),
    // Visibility gate — controls public discovery and class listings
    isActive: boolean('is_active').default(true).notNull(),
    // Sample/demo creator — shown only in its own locality view, hidden from
    // region-wide "All" browse and search.
    isExample: boolean('is_example').default(false).notNull(),
    localityId: varchar('locality_id').references(() => localities.id), // creator home locality
    icsToken: varchar('ics_token')
      .notNull()
      .default(sql`gen_random_uuid()`),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_creators_user_id').on(table.userId),
    index('idx_creators_verified').on(table.isVerified),
    index('idx_creators_featured').on(table.isFeatured),
    index('idx_creators_partner').on(table.isPartner),
    index('idx_creators_active').on(table.isActive),
    index('idx_creators_locality_id').on(table.localityId),
  ]
);

// Categories for classes
export const categories = pgTable('categories', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: varchar('name').notNull(),
  slug: varchar('slug').notNull().unique(),
  description: text('description'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Classes/courses
export const classes = pgTable(
  'classes',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    creatorId: varchar('creator_id')
      .references(() => creators.id)
      .notNull(),
    categoryId: varchar('category_id').references(() => categories.id),
    title: varchar('title').notNull(),
    description: text('description'),
    price: decimal('price', { precision: 10, scale: 2 }).notNull(),
    difficulty: varchar('difficulty'), // beginner, intermediate, advanced
    duration: integer('duration'), // in minutes
    thumbnailUrl: varchar('thumbnail_url'),
    galleryImages: jsonb('gallery_images').$type<string[]>(),
    videoUrl: varchar('video_url'),
    location: text('location'), // Physical address (use delivery_mode for online/in-person distinction)
    locationDetails: text('location_details'), // Additional location info (room number, special instructions, etc.)
    localityId: varchar('locality_id').references(() => localities.id), // required for in_person/hybrid; null for online-only
    venueId: varchar('venue_id').references(() => classLocations.id), // optional named venue within the locality
    deliveryMode: varchar('delivery_mode').notNull().default('in_person'), // in_person | online | hybrid
    // Companion online/livestream variant of this class, shown on the detail
    // page so students can join remotely when sold out or unable to attend.
    livestreamClassId: varchar('livestream_class_id'),
    maxStudents: integer('max_students'),
    pricingMode: varchar('pricing_mode').default('standard').notNull(), // standard | free | pay_what_you_want
    suggestedPrice: decimal('suggested_price', { precision: 10, scale: 2 }), // PWYW hint only
    promoEnabled: boolean('promo_enabled').default(false).notNull(), // temporary promo/launch discount
    promoPrice: decimal('promo_price', { precision: 10, scale: 2 }), // discounted price when promoEnabled (0 = free)
    // Optional date window for the promo, judged by the SESSION start date.
    // Both null = promo applies to every session (always-on).
    promoStartsAt: timestamp('promo_starts_at'),
    promoEndsAt: timestamp('promo_ends_at'),
    feedbackEmailEnabled: boolean('feedback_email_enabled').default(false).notNull(),
    cancellationPolicy: varchar('cancellation_policy').default('48_hours_before').notNull(),
    // Hours before a session that self-service booking closes; null = open until start.
    registrationCutoffHours: integer('registration_cutoff_hours'),
    registrationCutoffOnlyIfEmpty: boolean('registration_cutoff_only_if_empty')
      .default(false)
      .notNull(),
    isPublished: boolean('is_published').default(false).notNull(),
    isArchived: boolean('is_archived').default(false).notNull(),
    // Sample/demo listing — shown only in its own locality view, hidden from
    // region-wide "All" browse and search.
    isExample: boolean('is_example').default(false).notNull(),
    featured: boolean('featured').default(false).notNull(),
    rating: decimal('rating', { precision: 3, scale: 2 }),
    enrollmentCount: integer('enrollment_count').default(0).notNull(),
    // Recurrence
    recurrenceRule: varchar('recurrence_rule'), // iCal RRULE string, null = non-recurring
    recurrenceEndsAt: timestamp('recurrence_ends_at'), // null = rolling 8-week window
    recurrenceStartTime: varchar('recurrence_start_time', { length: 5 }), // HH:MM wall-clock time in the class locality's timezone (default America/Indiana/Indianapolis), e.g. "19:30". Not UTC — the generator resolves the offset per date so DST doesn't shift it.
    // Punch pass — a read-through mirror of this class's pass product
    // (pass_products), kept in sync by syncClassPassFields. Read paths use these;
    // writes go through server/lib/pass-products.ts.
    passEnabled: boolean('pass_enabled').default(false).notNull(),
    passSessionCount: integer('pass_session_count'), // sessions included in one pass
    passPrice: integer('pass_price'), // price in cents
    passExpiryDays: integer('pass_expiry_days'), // days from purchase, null = no expiry
    requiresWaiver: boolean('requires_waiver').default(false).notNull(),
    waiverTitle: varchar('waiver_title'),
    waiverText: text('waiver_text'),
    waiverVersion: integer('waiver_version').default(1).notNull(),
    // Ambassador assignment (per class). When set, this ambassador is paid a
    // referral commission carved from Kalovate's cut on this class's orders.
    // Overrides any creator-level ambassador_referrals fallback.
    ambassadorUserId: varchar('ambassador_user_id').references(() => users.id),
    ambassadorCommissionMode: varchar('ambassador_commission_mode'), // pct_of_net | fixed
    ambassadorCommissionPct: decimal('ambassador_commission_pct', { precision: 5, scale: 2 }),
    ambassadorCommissionFixedCents: integer('ambassador_commission_fixed_cents'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_classes_creator_id').on(table.creatorId),
    index('idx_classes_category_id').on(table.categoryId),
    index('idx_classes_published').on(table.isPublished),
    index('idx_classes_archived').on(table.isArchived),
    index('idx_classes_price').on(table.price),
    index('idx_classes_created_at').on(table.createdAt),
    index('idx_classes_locality_id').on(table.localityId),
    index('idx_classes_delivery_mode').on(table.deliveryMode),
  ]
);

// Class sessions/schedules - multiple scheduled sessions per class
export const classSessions = pgTable(
  'class_sessions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    classId: varchar('class_id')
      .references(() => classes.id)
      .notNull(),
    startDateTime: timestamp('start_date_time').notNull(),
    endDateTime: timestamp('end_date_time').notNull(),
    maxStudents: integer('max_students'), // Maximum capacity for this session
    isActive: boolean('is_active').default(true).notNull(),
    notes: text('notes'), // Session-specific notes
    disablePostClassEmail: boolean('disable_post_class_email').default(false).notNull(),
    seatHoldCount: integer('seat_hold_count').default(0).notNull(), // aggregate across active checkout_sessions
    isCancelled: boolean('is_cancelled').default(false).notNull(),
    cancelledAt: timestamp('cancelled_at'),
    cancellationNote: text('cancellation_note'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_class_sessions_class_id').on(table.classId),
    index('idx_class_sessions_start_time').on(table.startDateTime),
    index('idx_class_sessions_active').on(table.isActive),
  ]
);

// People who asked to be notified when a class with no bookable session
// (none scheduled, or every upcoming session full) opens a new one.
export const classWaitlistEntries = pgTable(
  'class_waitlist_entries',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    classId: varchar('class_id')
      .notNull()
      .references(() => classes.id, { onDelete: 'cascade' }),
    userId: varchar('user_id').references(() => users.id), // null for guest signups
    email: varchar('email', { length: 320 }).notNull(),
    name: varchar('name', { length: 200 }),
    unsubscribeToken: varchar('unsubscribe_token', { length: 64 })
      .notNull()
      .default(sql`replace(gen_random_uuid()::text, '-', '')`),
    notifiedAt: timestamp('notified_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('uidx_class_waitlist_class_email').on(table.classId, table.email),
    index('idx_class_waitlist_class_id').on(table.classId),
    index('idx_class_waitlist_token').on(table.unsubscribeToken),
  ]
);
export type ClassWaitlistEntry = typeof classWaitlistEntries.$inferSelect;

// Class enrollments
export const enrollments = pgTable(
  'enrollments',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    classId: varchar('class_id')
      .references(() => classes.id)
      .notNull(),
    sessionId: varchar('session_id').references(() => classSessions.id),
    orderId: varchar('order_id'), // FK to orders — added via migration (orders defined below)
    stripePaymentIntentId: varchar('stripe_payment_intent_id'),
    quantity: integer('quantity').default(1).notNull(),
    status: varchar('status').default('active').notNull(), // active, canceled, refunded
    reminderEnabled: boolean('reminder_enabled').default(true).notNull(),
    enrolledAt: timestamp('enrolled_at').defaultNow().notNull(),
  },
  table => [
    index('idx_enrollments_user_id').on(table.userId),
    index('idx_enrollments_class_id').on(table.classId),
    index('idx_enrollments_session_id').on(table.sessionId),
    index('idx_enrollments_reminder_enabled').on(table.reminderEnabled),
    index('idx_enrollments_order_id').on(table.orderId),
    index('idx_enrollments_enrolled_at').on(table.enrolledAt),
    // Partial unique: at most one ACTIVE enrollment per user per session. Prevents
    // duplicate active enrollments (e.g. double-clicked pass redemption) while still
    // allowing a canceled/refunded student to rebook the same session.
    uniqueIndex('uidx_enrollments_active_user_session')
      .on(table.userId, table.sessionId)
      .where(sql`status = 'active'`),
  ]
);

// Creator tips/support
export const tips = pgTable(
  'tips',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    creatorId: varchar('creator_id')
      .references(() => creators.id)
      .notNull(),
    sessionId: varchar('session_id').references(() => classSessions.id), // optional session association
    amount: decimal('amount', { precision: 10, scale: 2 }).notNull(),
    currency: varchar('currency').default('usd').notNull(),
    status: varchar('status').default('paid').notNull(), // paid | refunded
    message: text('message'),
    stripePaymentIntentId: varchar('stripe_payment_intent_id'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_tips_creator_id').on(table.creatorId),
    index('idx_tips_user_id').on(table.userId),
    index('idx_tips_created_at').on(table.createdAt),
  ]
);

// Featured logos for rotation
export const featuredLogos = pgTable('featured_logos', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  creatorId: varchar('creator_id')
    .references(() => creators.id)
    .notNull(),
  imageUrl: varchar('image_url').notNull(),
  title: varchar('title'),
  description: text('description'),
  isActive: boolean('is_active').default(true).notNull(),
  displayOrder: integer('display_order'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Calendar events (non-class community events)
export const CALENDAR_CATEGORIES = [
  'class_session',
  'farmers_market',
  'community',
  'library',
  'workshop',
  'arts',
  'sports',
  'food_drink',
  'outdoor',
  'government',
  'wellness',
  'music',
  'other',
] as const;

export type CalendarCategory = (typeof CALENDAR_CATEGORIES)[number];

export const calendarEvents = pgTable(
  'calendar_events',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    title: varchar('title').notNull(),
    description: text('description'),
    startsAt: timestamp('starts_at').notNull(),
    endsAt: timestamp('ends_at'),
    regionId: varchar('region_id').references(() => regions.id),
    localityId: varchar('locality_id').references(() => localities.id),
    creatorId: varchar('creator_id').references(() => creators.id),
    createdBy: varchar('created_by').references(() => users.id),
    isPublished: boolean('is_published').notNull().default(false),
    category: varchar('category', { length: 50 }).notNull().default('community'),
    tags: text('tags')
      .array()
      .notNull()
      .default(sql`ARRAY[]::text[]`),
    venueId: varchar('venue_id').references(() => classLocations.id),
    externalUrl: varchar('external_url', { length: 500 }),
    coverImageUrl: varchar('cover_image_url', { length: 500 }),
    isFeatured: boolean('is_featured').notNull().default(false),
    featuredOrder: integer('featured_order'),
    source: varchar('source', { length: 100 }).notNull().default('admin'),
    sourceName: varchar('source_name', { length: 150 }),
    attendanceMode: varchar('attendance_mode', { length: 20 }).notNull().default('in_person'),
    eventStatus: varchar('event_status', { length: 20 }).notNull().default('scheduled'),
    // External feed deduplication
    externalUid: varchar('external_uid', { length: 500 }),
    externalFeedId: varchar('external_feed_id'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_calendar_events_region_id').on(table.regionId),
    index('idx_calendar_events_locality_id').on(table.localityId),
    index('idx_calendar_events_starts_at').on(table.startsAt),
    index('idx_calendar_events_category').on(table.category),
    index('idx_calendar_events_venue_id').on(table.venueId),
    uniqueIndex('idx_calendar_events_external_uid')
      .on(table.externalFeedId, table.externalUid)
      .where(sql`${table.externalFeedId} IS NOT NULL AND ${table.externalUid} IS NOT NULL`),
  ]
);

// External ICS feed subscriptions for auto-importing calendar events
export const calendarEventFeeds = pgTable('calendar_event_feeds', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: varchar('name', { length: 200 }).notNull(),
  icalUrl: varchar('ical_url', { length: 1000 }).notNull(),
  regionId: varchar('region_id').references(() => regions.id),
  localityId: varchar('locality_id').references(() => localities.id),
  defaultCategory: varchar('default_category', { length: 50 }).notNull().default('community'),
  defaultVenueId: varchar('default_venue_id').references(() => classLocations.id),
  sourceName: varchar('source_name', { length: 150 }),
  lastSyncedAt: timestamp('last_synced_at'),
  lastSyncCount: integer('last_sync_count'),
  lastSyncStatus: varchar('last_sync_status', { length: 20 }), // ok | error
  lastSyncError: text('last_sync_error'),
  isActive: boolean('is_active').notNull().default(true),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

export type CalendarEventFeed = typeof calendarEventFeeds.$inferSelect;
export type InsertCalendarEventFeed = typeof calendarEventFeeds.$inferInsert;

// Recurring event series template
export const calendarEventSeries = pgTable(
  'calendar_event_series',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    title: varchar('title').notNull(),
    description: text('description'),
    category: varchar('category', { length: 50 }).notNull().default('community'),
    tags: text('tags')
      .array()
      .notNull()
      .default(sql`ARRAY[]::text[]`),
    venueId: varchar('venue_id').references(() => classLocations.id),
    externalUrl: varchar('external_url', { length: 500 }),
    coverImageUrl: varchar('cover_image_url', { length: 500 }),
    rrule: varchar('rrule', { length: 500 }).notNull(),
    dtstart: varchar('dtstart', { length: 20 }).notNull(), // local wall-clock: "2026-01-03T08:00:00"
    timezone: varchar('timezone', { length: 60 }).notNull().default('America/Indiana/Indianapolis'),
    durationMinutes: integer('duration_minutes').notNull().default(60),
    regionId: varchar('region_id'),
    localityId: varchar('locality_id'),
    creatorId: varchar('creator_id').references(() => creators.id),
    createdBy: varchar('created_by')
      .notNull()
      .references(() => users.id),
    status: varchar('status', { length: 20 }).notNull().default('draft'), // draft|preview|published|archived
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_calendar_series_region_id').on(table.regionId),
    index('idx_calendar_series_locality_id').on(table.localityId),
    index('idx_calendar_series_status').on(table.status),
  ]
);

// Pre-materialized recurring event instances
export const calendarInstances = pgTable(
  'calendar_instances',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    seriesId: varchar('series_id')
      .notNull()
      .references(() => calendarEventSeries.id),
    originalStartsAt: timestamp('original_starts_at').notNull(), // rrule-generated, never changes
    startsAt: timestamp('starts_at').notNull(),
    endsAt: timestamp('ends_at'),
    timezone: varchar('timezone', { length: 60 }).notNull(),
    isException: boolean('is_exception').notNull().default(false),
    exceptionType: varchar('exception_type', { length: 20 }), // modified | cancelled | null
    status: varchar('status', { length: 20 }).notNull().default('scheduled'),
    // Denormalized from series so public queries need no JOIN to calendar_event_series
    title: varchar('title').notNull(),
    description: text('description'),
    category: varchar('category', { length: 50 }).notNull().default('community'),
    tags: text('tags')
      .array()
      .notNull()
      .default(sql`ARRAY[]::text[]`),
    attendanceMode: varchar('attendance_mode', { length: 20 }).notNull().default('in_person'),
    source: varchar('source', { length: 100 }).notNull().default('series'),
    coverImageUrl: varchar('cover_image_url', { length: 500 }),
    externalUrl: varchar('external_url', { length: 500 }),
    creatorId: varchar('creator_id'),
    regionId: varchar('region_id'),
    localityId: varchar('locality_id'),
    venueId: varchar('venue_id'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('idx_calendar_instances_series_original').on(
      table.seriesId,
      table.originalStartsAt
    ),
    index('idx_calendar_instances_region_starts').on(table.regionId, table.startsAt),
    index('idx_calendar_instances_locality_starts').on(table.localityId, table.startsAt),
    index('idx_calendar_instances_venue_starts').on(table.venueId, table.startsAt),
  ]
);

export type CalendarEventSeries = typeof calendarEventSeries.$inferSelect;
export type InsertCalendarEventSeries = typeof calendarEventSeries.$inferInsert;
export type CalendarInstance = typeof calendarInstances.$inferSelect;
export type InsertCalendarInstance = typeof calendarInstances.$inferInsert;

// Private metadata for community-submitted events (PII not exposed by public API)
export const calendarEventSubmissions = pgTable(
  'calendar_event_submissions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    eventId: varchar('event_id').references(() => calendarEvents.id, { onDelete: 'set null' }),
    submitterName: varchar('submitter_name', { length: 200 }).notNull(),
    submitterEmail: varchar('submitter_email', { length: 320 }).notNull(),
    submitterMessage: text('submitter_message'),
    submittedIpHash: varchar('submitted_ip_hash', { length: 64 }),
    submittedUserAgent: varchar('submitted_user_agent', { length: 500 }),
    reviewedBy: varchar('reviewed_by').references(() => users.id, { onDelete: 'set null' }),
    reviewedAt: timestamp('reviewed_at'),
    reviewDecision: varchar('review_decision', { length: 20 }), // approved | rejected | null
    reviewNotes: text('review_notes'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_calendar_submissions_decision').on(table.reviewDecision),
    index('idx_calendar_submissions_created').on(table.createdAt),
  ]
);

export type CalendarEventSubmission = typeof calendarEventSubmissions.$inferSelect;
export type InsertCalendarEventSubmission = typeof calendarEventSubmissions.$inferInsert;

// Saved calendar events (bookmarks) — users can save events and get reminder emails
export const calendarEventSaves = pgTable(
  'calendar_event_saves',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    eventId: varchar('event_id')
      .notNull()
      .references(() => calendarEvents.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('idx_cal_saves_user_event').on(table.userId, table.eventId),
    index('idx_cal_saves_event').on(table.eventId),
  ]
);

export type CalendarEventSave = typeof calendarEventSaves.$inferSelect;

// RSVP / interest state per user per event
export const calendarEventRsvps = pgTable(
  'calendar_event_rsvps',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    eventId: varchar('event_id')
      .notNull()
      .references(() => calendarEvents.id, { onDelete: 'cascade' }),
    status: varchar('status').notNull(), // 'going' | 'interested'
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('idx_cal_rsvps_user_event').on(table.userId, table.eventId),
    index('idx_cal_rsvps_event').on(table.eventId),
  ]
);

export type CalendarEventRsvp = typeof calendarEventRsvps.$inferSelect;

// Weekly digest subscriptions — email opt-in per locality (no auth required)
export const calendarDigestSubscriptions = pgTable(
  'calendar_digest_subscriptions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    email: varchar('email', { length: 320 }).notNull(),
    name: varchar('name', { length: 200 }),
    localityId: varchar('locality_id')
      .notNull()
      .references(() => localities.id, { onDelete: 'cascade' }),
    unsubscribeToken: varchar('unsubscribe_token', { length: 64 })
      .notNull()
      .default(sql`replace(gen_random_uuid()::text, '-', '')`),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('idx_cal_digest_email_locality').on(table.email, table.localityId),
    index('idx_cal_digest_locality').on(table.localityId),
    index('idx_cal_digest_token').on(table.unsubscribeToken),
  ]
);

export type CalendarDigestSubscription = typeof calendarDigestSubscriptions.$inferSelect;

// Community topics with explicit region, locality, or class scope
export const communityTopics = pgTable(
  'community_topics',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    scopeType: varchar('scope_type').notNull(), // region | locality | class
    regionId: varchar('region_id').references(() => regions.id),
    localityId: varchar('locality_id').references(() => localities.id),
    classId: varchar('class_id').references(() => classes.id),
    visibility: varchar('visibility').notNull().default('public'), // public | enrolled
    title: varchar('title').notNull(),
    content: text('content'),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id),
    isSticky: boolean('is_sticky').notNull().default(false),
    isLocked: boolean('is_locked').notNull().default(false),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
    searchVector: tsvector('search_vector'),
  },
  table => [
    index('idx_community_topics_region_id').on(table.regionId),
    index('idx_community_topics_locality_id').on(table.localityId),
    index('idx_community_topics_class_id').on(table.classId),
    index('idx_community_topics_user_id').on(table.userId),
  ]
);

// Community replies
export const communityReplies = pgTable(
  'community_replies',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    topicId: varchar('topic_id')
      .notNull()
      .references(() => communityTopics.id),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id),
    content: text('content').notNull(),
    isDeleted: boolean('is_deleted').notNull().default(false),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_community_replies_topic_id').on(table.topicId),
    index('idx_community_replies_user_id').on(table.userId),
  ]
);

// Community reports — user flags on topics or replies
export const communityReports = pgTable('community_reports', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  reporterId: varchar('reporter_id')
    .notNull()
    .references(() => users.id),
  topicId: varchar('topic_id').references(() => communityTopics.id),
  replyId: varchar('reply_id').references(() => communityReplies.id),
  reason: varchar('reason').notNull(),
  status: varchar('status').notNull().default('pending'), // pending | dismissed | actioned
  adminNotes: text('admin_notes'),
  reviewedBy: varchar('reviewed_by').references(() => users.id),
  reviewedAt: timestamp('reviewed_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Community likes — per-user thumbs-up on topics or replies
export const communityLikes = pgTable(
  'community_likes',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id),
    topicId: varchar('topic_id').references(() => communityTopics.id),
    replyId: varchar('reply_id').references(() => communityReplies.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_community_likes_user_id').on(table.userId),
    index('idx_community_likes_topic_id').on(table.topicId),
    index('idx_community_likes_reply_id').on(table.replyId),
    uniqueIndex('uidx_community_likes_user_topic').on(table.userId, table.topicId),
    uniqueIndex('uidx_community_likes_user_reply').on(table.userId, table.replyId),
  ]
);

// Class reviews — one per (user, class) after attending at least one session
export const classReviews = pgTable(
  'class_reviews',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id),
    classId: varchar('class_id')
      .notNull()
      .references(() => classes.id),
    orderId: varchar('order_id').references(() => orders.id),
    rating: integer('rating').notNull(), // 1–5
    body: text('body'),
    isPublished: boolean('is_published').notNull().default(true),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('uidx_class_reviews_user_class').on(table.userId, table.classId),
    index('idx_class_reviews_class_id').on(table.classId),
    index('idx_class_reviews_user_id').on(table.userId),
  ]
);

// A punch pass offering: what's sold (punch count + price) and which classes
// it's good for. Owned for payout purposes by one creator (the payee) and
// optionally anchored to a venue so a venue can offer one pass across several
// of its classes — e.g. "Fig Tree Yoga Pass" covering every yoga class at Fig
// Tree Market but not the coffee class there. Deactivating a product stops new
// sales; passes already sold keep working for its classes.
export const passProducts = pgTable(
  'pass_products',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    name: varchar('name').notNull(),
    description: text('description'),
    // Payee for the sale split. Until per-punch settlement exists, every class a
    // product covers must belong to this creator (enforced in pass-products.ts).
    creatorId: varchar('creator_id')
      .notNull()
      .references(() => creators.id),
    venueId: varchar('venue_id').references(() => classLocations.id), // null = not venue-scoped
    sessionCount: integer('session_count').notNull(), // punches in one pass
    priceCents: integer('price_cents').notNull(),
    isActive: boolean('is_active').default(true).notNull(),
    createdBy: varchar('created_by').references(() => users.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_pass_products_creator_id').on(table.creatorId),
    index('idx_pass_products_venue_id').on(table.venueId),
  ]
);

// Which classes a pass product covers. A class belongs to at most one product.
export const passProductClasses = pgTable(
  'pass_product_classes',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    passProductId: varchar('pass_product_id')
      .notNull()
      .references(() => passProducts.id),
    classId: varchar('class_id')
      .notNull()
      .references(() => classes.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_pass_product_classes_product_id').on(table.passProductId),
    uniqueIndex('uidx_pass_product_classes_class_id').on(table.classId),
  ]
);

// Punch passes purchased by students. Usable for every class the pass product
// covers; class_id records the class it was bought from.
export const userPasses = pgTable(
  'user_passes',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id),
    classId: varchar('class_id')
      .notNull()
      .references(() => classes.id),
    // Null only on passes sold before pass products existed and not yet
    // backfilled; those fall back to matching class_id.
    passProductId: varchar('pass_product_id').references(() => passProducts.id),
    orderId: varchar('order_id').references(() => orders.id),
    totalSessions: integer('total_sessions').notNull(),
    usedSessions: integer('used_sessions').default(0).notNull(),
    expiresAt: timestamp('expires_at'), // null = no expiry
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_user_passes_user_id').on(table.userId),
    index('idx_user_passes_class_id').on(table.classId),
    index('idx_user_passes_pass_product_id').on(table.passProductId),
    index('idx_user_passes_order_id').on(table.orderId),
  ]
);

// Tracks which session each pass punch was used for
export const passRedemptions = pgTable(
  'pass_redemptions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userPassId: varchar('user_pass_id')
      .notNull()
      .references(() => userPasses.id),
    classSessionId: varchar('class_session_id')
      .notNull()
      .references(() => classSessions.id),
    enrollmentId: varchar('enrollment_id').references(() => enrollments.id),
    // The specific seat this punch paid for. Null on redemptions made before
    // this column existed. One punch per seat — a seat can't be punched twice.
    attendeeId: varchar('attendee_id').references(() => enrollmentAttendees.id),
    redeemedAt: timestamp('redeemed_at').defaultNow().notNull(),
  },
  table => [
    index('idx_pass_redemptions_user_pass_id').on(table.userPassId),
    index('idx_pass_redemptions_session_id').on(table.classSessionId),
    // Superseded by uidx_pass_redemptions_attendee below (which allows more
    // than one punch per session — e.g. a punch for a guest's seat too — but
    // still forbids the same seat from being punched twice). Kept as a plain,
    // non-unique lookup index since (pass, session) is still a common query.
    index('idx_pass_redemptions_pass_session').on(table.userPassId, table.classSessionId),
    uniqueIndex('uidx_pass_redemptions_attendee')
      .on(table.attendeeId)
      .where(sql`${table.attendeeId} IS NOT NULL`),
  ]
);

// Every balance change to a pass that isn't a plain booking/self-cancel: staff
// restores and removals, punches returned for a canceled booking or session,
// and transfers in/out. The audit trail for customer-service questions.
// user_passes stays the source of truth for the balance; each row here records
// one change to it.
export const passAdjustments = pgTable(
  'pass_adjustments',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userPassId: varchar('user_pass_id')
      .notNull()
      .references(() => userPasses.id),
    // Change to the student's remaining punches: + gives punches, − takes them.
    deltaPunches: integer('delta_punches').notNull(),
    // restore | remove | booking_returned | session_canceled | transfer_out |
    // transfer_in | transfer_returned | expiry_changed (delta 0)
    kind: varchar('kind').notNull(),
    reason: text('reason'),
    actorUserId: varchar('actor_user_id').references(() => users.id), // null = system
    enrollmentId: varchar('enrollment_id').references(() => enrollments.id),
    transferId: varchar('transfer_id'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_pass_adjustments_user_pass_id').on(table.userPassId),
    index('idx_pass_adjustments_transfer_id').on(table.transferId),
  ]
);

// Punches a student sends to someone else (a transfer or a gift). The punches
// leave the sender's pass when sent and are held here until the recipient
// claims them with the link (becoming a new pass on the same pass product) or
// the sender/staff cancels, which returns them.
export const passTransfers = pgTable(
  'pass_transfers',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    fromPassId: varchar('from_pass_id')
      .notNull()
      .references(() => userPasses.id),
    fromUserId: varchar('from_user_id')
      .notNull()
      .references(() => users.id),
    punches: integer('punches').notNull(),
    recipientEmail: varchar('recipient_email').notNull(),
    recipientName: varchar('recipient_name'),
    message: text('message'),
    status: varchar('status').default('pending').notNull(), // pending | claimed | canceled
    tokenHash: varchar('token_hash').notNull().unique(), // sha256 of the claim token
    toPassId: varchar('to_pass_id').references(() => userPasses.id),
    toUserId: varchar('to_user_id').references(() => users.id),
    canceledBy: varchar('canceled_by').references(() => users.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    claimedAt: timestamp('claimed_at'),
    canceledAt: timestamp('canceled_at'),
  },
  table => [
    index('idx_pass_transfers_from_user_id').on(table.fromUserId),
    index('idx_pass_transfers_from_pass_id').on(table.fromPassId),
    index('idx_pass_transfers_status').on(table.status),
  ]
);

// Newsletter subscriptions
export const newsletters = pgTable('newsletters', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: varchar('email').notNull().unique(),
  isActive: boolean('is_active').default(true).notNull(),
  subscribedAt: timestamp('subscribed_at').defaultNow().notNull(),
});

// Blog categories — admin-managed taxonomy for posts
export const blogCategories = pgTable(
  'blog_categories',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    name: varchar('name').notNull(),
    slug: varchar('slug').notNull().unique(),
    description: text('description'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [index('idx_blog_categories_slug').on(table.slug)]
);

// Blog posts — editorial content written by blog writers, published by admins
export const blogPosts = pgTable(
  'blog_posts',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    title: varchar('title').notNull(),
    slug: varchar('slug').notNull().unique(),
    excerpt: text('excerpt'),
    content: text('content').notNull(), // HTML from TipTap editor
    featuredImageUrl: varchar('featured_image_url'),
    authorId: varchar('author_id')
      .notNull()
      .references(() => users.id),
    categoryId: varchar('category_id').references(() => blogCategories.id),
    tags: jsonb('tags')
      .$type<string[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),
    // Geography scope — null = applies to all regions
    regionId: varchar('region_id').references(() => regions.id),
    localityId: varchar('locality_id').references(() => localities.id),
    // Workflow status: draft → pending_review → published (or rejected/archived)
    status: varchar('status').default('draft').notNull(),
    // draft | pending_review | published | rejected | archived
    publishedAt: timestamp('published_at'),
    scheduledFor: timestamp('scheduled_for'),
    // SEO
    seoTitle: varchar('seo_title'),
    seoDescription: text('seo_description'),
    // Email blast tracking
    emailSentAt: timestamp('email_sent_at'),
    // Promotion — admin can pin one article per region to the featured hero slot
    isPinned: boolean('is_pinned').default(false).notNull(),
    // Sample/demo post — shown only in its own locality view, hidden from
    // region-wide "All" updates and search.
    isExample: boolean('is_example').default(false).notNull(),
    // Review workflow
    reviewedBy: varchar('reviewed_by').references(() => users.id),
    reviewedAt: timestamp('reviewed_at'),
    rejectionNote: text('rejection_note'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_blog_posts_slug').on(table.slug),
    index('idx_blog_posts_status').on(table.status),
    index('idx_blog_posts_author_id').on(table.authorId),
    index('idx_blog_posts_region_id').on(table.regionId),
    index('idx_blog_posts_locality_id').on(table.localityId),
    index('idx_blog_posts_published_at').on(table.publishedAt),
    index('idx_blog_posts_category_id').on(table.categoryId),
  ]
);

// Geography-scoped blog email subscriptions
export const blogSubscriptions = pgTable(
  'blog_subscriptions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    email: varchar('email').notNull(),
    regionId: varchar('region_id')
      .notNull()
      .references(() => regions.id),
    isActive: boolean('is_active').default(true).notNull(),
    unsubscribeToken: varchar('unsubscribe_token').notNull().unique(),
    subscribedAt: timestamp('subscribed_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('uidx_blog_subscriptions_email_region').on(table.email, table.regionId),
    index('idx_blog_subscriptions_region_id').on(table.regionId),
    index('idx_blog_subscriptions_active').on(table.isActive),
  ]
);

// Partner applications
export const partnerApplications = pgTable('partner_applications', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar('user_id').references(() => users.id),
  name: varchar('name').notNull(),
  email: varchar('email').notNull(),
  phone: varchar('phone'),
  specialty: varchar('specialty').notNull(),
  experience: text('experience'),
  portfolio: text('portfolio'),
  message: text('message'),
  status: varchar('status').default('pending').notNull(), // pending, approved, rejected
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Page content management
export const pageContent = pgTable(
  'page_content',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    pageSlug: varchar('page_slug').notNull(),
    sectionKey: varchar('section_key').notNull(),
    contentType: varchar('content_type').notNull(), // 'text', 'html', 'json'
    contentValue: text('content_value').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [{ name: 'unique_page_section', columns: [table.pageSlug, table.sectionKey] }]
);

// Promo codes for discounts
export const promoCodes = pgTable('promo_codes', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  code: varchar('code').notNull().unique(),
  description: text('description'),
  discountType: varchar('discount_type').notNull(), // 'percentage', 'fixed'
  discountValue: decimal('discount_value', { precision: 10, scale: 2 }).notNull(),
  minimumAmount: decimal('minimum_amount', { precision: 10, scale: 2 }),
  classId: varchar('class_id').references(() => classes.id), // null = applies to all classes
  maxUses: integer('max_uses'),
  usedCount: integer('used_count').default(0).notNull(),
  isActive: boolean('is_active').default(true).notNull(),
  firstUseOnly: boolean('first_use_only').default(false).notNull(),
  // Whether this code may be applied to punch-pass purchases. Off by default so
  // ordinary class promo codes never silently discount passes.
  appliesToPasses: boolean('applies_to_passes').default(false).notNull(),
  expiresAt: timestamp('expires_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Audit log — records high-risk admin and creator actions for compliance and support
export const auditLogs = pgTable(
  'audit_logs',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    actorId: varchar('actor_id')
      .notNull()
      .references(() => users.id),
    action: varchar('action').notNull(), // e.g. 'user.role_changed', 'class.deleted'
    targetType: varchar('target_type'), // e.g. 'user', 'class', 'enrollment'
    targetId: varchar('target_id'),
    metadata: jsonb('metadata'), // before/after state or relevant context
    ipAddress: varchar('ip_address'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('audit_logs_actor_id_idx').on(table.actorId),
    index('audit_logs_action_idx').on(table.action),
    index('audit_logs_created_at_idx').on(table.createdAt),
  ]
);

export const auditLogRelations = relations(auditLogs, ({ one }) => ({
  actor: one(users, { fields: [auditLogs.actorId], references: [users.id] }),
}));

export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = typeof auditLogs.$inferInsert;

// Creator applications — pending admin approval before role elevation
export const creatorApplications = pgTable(
  'creator_applications',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .notNull()
      .references(() => users.id),
    displayName: varchar('display_name').notNull(),
    phoneNumber: varchar('phone_number'),
    bio: text('bio'),
    creatorHeadline: varchar('specialty'),
    portfolioUrl: varchar('portfolio_url'),
    socialLinks: jsonb('social_links'),
    experience: text('experience'),
    status: varchar('status').notNull().default('pending'), // 'pending', 'approved', 'rejected'
    adminNotes: text('admin_notes'),
    reviewedBy: varchar('reviewed_by').references(() => users.id),
    reviewedAt: timestamp('reviewed_at'),
    // Phase 5 Item 8 additions
    proposedContent: text('proposed_content'),
    experienceLevel: varchar('experience_level'), // 'hobbyist' | 'semi_professional' | 'professional' | 'expert'
    hasExistingAudience: boolean('has_existing_audience'),
    audienceDescription: text('audience_description'),
    primaryLocation: varchar('primary_location'),
    referralSource: varchar('referral_source'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('creator_applications_user_id_idx').on(table.userId),
    index('creator_applications_status_idx').on(table.status),
  ]
);

export const creatorApplicationRelations = relations(creatorApplications, ({ one }) => ({
  user: one(users, { fields: [creatorApplications.userId], references: [users.id] }),
  reviewer: one(users, { fields: [creatorApplications.reviewedBy], references: [users.id] }),
}));

export type CreatorApplication = typeof creatorApplications.$inferSelect;
export type InsertCreatorApplication = typeof creatorApplications.$inferInsert;

// ─── Phase 1 tables ───────────────────────────────────────────────────────────

// Server-owned checkout state (temporary, expires in 1 hour)
export const checkoutSessions = pgTable(
  'checkout_sessions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    classId: varchar('class_id')
      .references(() => classes.id)
      .notNull(),
    sessionId: varchar('session_id')
      .references(() => classSessions.id)
      .notNull(),
    quantity: integer('quantity').default(1).notNull(),
    promoCode: varchar('promo_code'),
    pricingSnapshot: jsonb('pricing_snapshot').notNull(), // { unitPrice, discount, finalAmount, currency }
    attendeeDrafts: jsonb('attendee_drafts'), // [{ name, isGuest: true }] for extra seats
    status: varchar('status').default('active').notNull(), // active, completed, expired, abandoned
    seatHoldCount: integer('seat_hold_count').default(0).notNull(),
    expiresAt: timestamp('expires_at').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    // Expiry job polls status + expires_at constantly — partial index on active only
    index('idx_checkout_sessions_status_expires').on(table.status, table.expiresAt),
    index('idx_checkout_sessions_user_id').on(table.userId),
    index('idx_checkout_sessions_session_id').on(table.sessionId),
  ]
);

// Finalized order record
export const orders = pgTable(
  'orders',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    checkoutSessionId: varchar('checkout_session_id').references(() => checkoutSessions.id),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    orderType: varchar('order_type').default('class_enrollment').notNull(), // class_enrollment | tip
    classId: varchar('class_id').references(() => classes.id), // null for tip orders
    sessionId: varchar('session_id').references(() => classSessions.id), // null for tip orders
    tipId: varchar('tip_id').references(() => tips.id), // set for tip orders
    quantity: integer('quantity').notNull(),
    grossAmountCents: integer('gross_amount_cents').notNull(),
    discountCents: integer('discount_cents').default(0).notNull(),
    netAmountCents: integer('net_amount_cents').notNull(),
    currency: varchar('currency').default('usd').notNull(),
    paymentType: varchar('payment_type').notNull(), // stripe, free, zero_dollar
    stripePaymentIntentId: varchar('stripe_payment_intent_id').unique(),
    status: varchar('status').default('pending').notNull(),
    // pending, paid, failed, canceled, refunded, partially_refunded, expired
    cancellationPolicy: varchar('cancellation_policy'), // snapshot of class policy at purchase time
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_orders_stripe_payment_intent').on(table.stripePaymentIntentId),
    index('idx_orders_user_id').on(table.userId),
    index('idx_orders_session_id').on(table.sessionId),
    index('idx_orders_status').on(table.status),
    index('idx_orders_order_type').on(table.orderType),
  ]
);

// One row per seat (purchaser + guests)
export const enrollmentAttendees = pgTable(
  'enrollment_attendees',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    enrollmentId: varchar('enrollment_id')
      .references(() => enrollments.id)
      .notNull(),
    // Null for punch-pass-originated seats (no checkout order exists). Every
    // checkout-flow attendee still has one.
    orderId: varchar('order_id').references(() => orders.id),
    userId: varchar('user_id').references(() => users.id), // null for guests
    guestName: varchar('guest_name'), // populated for guests
    guestEmail: varchar('guest_email'), // populated for guests, e.g. punch-pass "bring a guest"
    isPurchaser: boolean('is_purchaser').default(false).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_enrollment_attendees_enrollment_id').on(table.enrollmentId),
    index('idx_enrollment_attendees_order_id').on(table.orderId),
  ]
);

export const classWaiverSignatures = pgTable(
  'class_waiver_signatures',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    classId: varchar('class_id')
      .notNull()
      .references(() => classes.id),
    sessionId: varchar('session_id').references(() => classSessions.id),
    enrollmentId: varchar('enrollment_id').references(() => enrollments.id),
    attendeeId: varchar('attendee_id').references(() => enrollmentAttendees.id),
    userId: varchar('user_id').references(() => users.id),
    signerName: varchar('signer_name'),
    signerEmail: varchar('signer_email'),
    signerPhone: varchar('signer_phone'),
    emergencyContactName: varchar('emergency_contact_name'),
    emergencyContactPhone: varchar('emergency_contact_phone'),
    ageConfirmed: boolean('age_confirmed').default(false).notNull(),
    mediaReleaseConsent: boolean('media_release_consent'),
    waiverVersion: integer('waiver_version').default(1).notNull(),
    waiverTitle: varchar('waiver_title'),
    waiverTextSnapshot: text('waiver_text_snapshot').notNull(),
    status: varchar('status').default('pending').notNull(), // pending | signed
    tokenHash: varchar('token_hash').notNull().unique(),
    tokenExpiresAt: timestamp('token_expires_at'),
    sentAt: timestamp('sent_at'),
    lastReminderAt: timestamp('last_reminder_at'),
    signedAt: timestamp('signed_at'),
    ipAddress: varchar('ip_address'),
    userAgent: text('user_agent'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_class_waivers_class_id').on(table.classId),
    index('idx_class_waivers_session_id').on(table.sessionId),
    index('idx_class_waivers_enrollment_id').on(table.enrollmentId),
    index('idx_class_waivers_attendee_id').on(table.attendeeId),
    index('idx_class_waivers_user_class_version').on(
      table.userId,
      table.classId,
      table.waiverVersion
    ),
    index('idx_class_waivers_status').on(table.status),
  ]
);

export const creatorStudents = pgTable(
  'creator_students',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    creatorId: varchar('creator_id')
      .notNull()
      .references(() => creators.id),
    name: varchar('name').notNull(),
    email: varchar('email').notNull(),
    phoneNumber: varchar('phone_number'),
    notes: text('notes'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_creator_students_creator_id').on(table.creatorId),
    index('idx_creator_students_email').on(table.email),
  ]
);

export const refundRequests = pgTable(
  'refund_requests',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    orderId: varchar('order_id')
      .references(() => orders.id)
      .notNull(),
    userId: varchar('user_id')
      .references(() => users.id)
      .notNull(),
    classId: varchar('class_id')
      .references(() => classes.id)
      .notNull(),
    sessionId: varchar('session_id')
      .references(() => classSessions.id)
      .notNull(),
    creatorId: varchar('creator_id')
      .references(() => creators.id)
      .notNull(),
    status: varchar('status').default('pending').notNull(), // pending, creator_review, auto_approved, approved, denied, canceled
    cancellationPolicy: varchar('cancellation_policy').notNull(),
    requestedReason: text('requested_reason'),
    denialReason: text('denial_reason'),
    reviewedBy: varchar('reviewed_by').references(() => users.id),
    reviewedAt: timestamp('reviewed_at'),
    refundAmountCents: integer('refund_amount_cents').notNull(),
    // Who absorbs the non-refundable card processing fee on approval:
    // customer (partial refund) | creator | platform (full refund). Set when processed.
    feeBearer: varchar('fee_bearer'),
    // False while within the cancellation-policy window (cannot be denied — must
    // be refunded full or partial); true once past the window or for no_refund.
    denyAllowed: boolean('deny_allowed').default(false).notNull(),
    stripeRefundId: varchar('stripe_refund_id'),
    processedAt: timestamp('processed_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    uniqueIndex('uidx_refund_requests_order_id').on(table.orderId),
    index('idx_refund_requests_status').on(table.status),
    index('idx_refund_requests_creator_id').on(table.creatorId),
    index('idx_refund_requests_user_id').on(table.userId),
    index('idx_refund_requests_session_id').on(table.sessionId),
  ]
);

// ─── Phase 1B financial tables ───────────────────────────────────────────────

// Fee configuration — global or per-creator/class overrides
export const feeProfiles = pgTable('fee_profiles', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  scope: varchar('scope').notNull(), // global | creator | class
  scopeId: varchar('scope_id'), // creator_id or class_id when scope != global
  classSaleCreatorPct: decimal('class_sale_creator_pct', { precision: 5, scale: 2 })
    .default('90.00')
    .notNull(),
  // Venue share of a class sale (0 when the class has no venue). Kalovate keeps
  // 100 − creator% − venue% (minus Stripe processing fees, which Kalovate absorbs).
  classSaleVenuePct: decimal('class_sale_venue_pct', { precision: 5, scale: 2 })
    .default('0.00')
    .notNull(),
  tipCreatorPct: decimal('tip_creator_pct', { precision: 5, scale: 2 }).default('95.00').notNull(),
  // Informational only — the split logic always has the platform absorb Stripe
  // processing fees ("Kalovate absorbs fees" model in financial-snapshot.ts).
  // Not read by any code path; kept for audit/config clarity.
  processingFeeBearer: varchar('processing_fee_bearer').default('platform').notNull(), // creator | platform
  // Venue payout mode: percentage of net (classSaleVenuePct) OR a fixed per-session
  // rental that accrues across that session's ticket sales.
  venuePayoutMode: varchar('venue_payout_mode').default('pct').notNull(), // pct | fixed
  venueFixedCentsPerSession: integer('venue_fixed_cents_per_session'),
  // Optional guaranteed platform margin (on top of the Stripe fee) before any
  // ambassador share is carved from Kalovate's cut. 0 = only reserve the fee.
  minPlatformFloorCents: integer('min_platform_floor_cents').default(0).notNull(),
  payoutDelayHours: integer('payout_delay_hours').default(48).notNull(),
  effectiveFrom: timestamp('effective_from').defaultNow().notNull(),
  effectiveUntil: timestamp('effective_until'), // null = current
  createdBy: varchar('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Ambassador → creator referral link. An ambassador earns a referral commission
// (carved from Kalovate's cut) on the referred creator's sales. Class-level
// assignment (classes.ambassador_*) takes precedence over this fallback.
export const ambassadorReferrals = pgTable(
  'ambassador_referrals',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    ambassadorUserId: varchar('ambassador_user_id')
      .references(() => users.id)
      .notNull(),
    creatorId: varchar('creator_id')
      .references(() => creators.id)
      .notNull(),
    commissionMode: varchar('commission_mode').notNull(), // pct_of_net | fixed
    commissionPct: decimal('commission_pct', { precision: 5, scale: 2 }),
    commissionFixedCents: integer('commission_fixed_cents'),
    status: varchar('status').default('active').notNull(), // active | ended
    startsAt: timestamp('starts_at').defaultNow().notNull(),
    endsAt: timestamp('ends_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_ambassador_referrals_ambassador').on(table.ambassadorUserId),
    index('idx_ambassador_referrals_creator').on(table.creatorId),
  ]
);

// Per-order financial snapshot — written at payment confirmation
export const orderFinancials = pgTable(
  'order_financials',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    orderId: varchar('order_id')
      .notNull()
      .unique()
      .references(() => orders.id),
    idempotencyKey: varchar('idempotency_key').unique(), // stripe_payment_intent_id
    stripePaymentIntentId: varchar('stripe_payment_intent_id'),
    stripeChargeId: varchar('stripe_charge_id'),
    transferGroup: varchar('transfer_group'),
    grossAmountCents: integer('gross_amount_cents').notNull(),
    discountCents: integer('discount_cents').default(0).notNull(),
    netCollectedCents: integer('net_collected_cents').notNull(),
    processingFeeCents: integer('processing_fee_cents').default(0).notNull(),
    platformFeeCents: integer('platform_fee_cents').notNull(),
    creatorEarningsCents: integer('creator_earnings_cents').notNull(),
    venueEarningsCents: integer('venue_earnings_cents').default(0).notNull(),
    venueConnectedAccountId: varchar('venue_connected_account_id'),
    tipAmountCents: integer('tip_amount_cents').default(0).notNull(),
    refundTotalCents: integer('refund_total_cents').default(0).notNull(),
    chargebackTotalCents: integer('chargeback_total_cents').default(0).notNull(),
    // Ambassador referral commission for this order (carved from the platform cut).
    ambassadorEarningsCents: integer('ambassador_earnings_cents').default(0).notNull(),
    // Sales tax collected as a pass-through (added on top; NOT part of the split).
    taxCollectedCents: integer('tax_collected_cents').default(0).notNull(),
    // True when fixed fees/rental could not be fully covered by this order.
    underfunded: boolean('underfunded').default(false).notNull(),
    feeProfileId: varchar('fee_profile_id').references(() => feeProfiles.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_order_financials_order_id').on(table.orderId),
    index('idx_order_financials_stripe_pi').on(table.stripePaymentIntentId),
  ]
);

// Creator earnings ledger — positive = earned, negative = reversed/deducted
export const creatorLedgerEntries = pgTable(
  'creator_ledger_entries',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    // Null for venue_owner entries (venue owners are users, not creators).
    creatorId: varchar('creator_id').references(() => creators.id),
    // Generalized payee identity. payeeId = creatorId for creator entries,
    // owner userId for venue_owner entries.
    payeeType: varchar('payee_type').default('creator').notNull(), // creator | venue_owner
    payeeId: varchar('payee_id'),
    entryType: varchar('entry_type').notNull(),
    // class_sale | tip | refund | chargeback | manual_adjustment | payout
    amountCents: integer('amount_cents').notNull(), // positive = earned, negative = deducted
    orderId: varchar('order_id').references(() => orders.id),
    payoutBatchId: varchar('payout_batch_id'), // FK added when payout_batches table exists
    idempotencyKey: varchar('idempotency_key').unique(), // entry_type:order_id or entry_type:stripe_event_id
    referenceNote: text('reference_note'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_creator_ledger_entries_creator_id').on(table.creatorId),
    index('idx_creator_ledger_entries_order_id').on(table.orderId),
    index('idx_creator_ledger_entries_type').on(table.entryType),
  ]
);

// Stripe Connect connected accounts
export const connectedAccounts = pgTable(
  'connected_accounts',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    // Nullable now that venue_owner accounts (which have no creator) are supported.
    creatorId: varchar('creator_id')
      .unique()
      .references(() => creators.id),
    // Generalized payee identity. payeeId = creatorId for creator accounts,
    // owner userId for venue accounts. This is the canonical key going forward.
    payeeType: varchar('payee_type').default('creator').notNull(), // creator | venue_owner
    payeeId: varchar('payee_id'),
    stripeAccountId: varchar('stripe_account_id').notNull().unique(),
    onboardingStatus: varchar('onboarding_status').default('not_started').notNull(),
    // not_started | in_progress | complete | deauthorized
    payoutsEnabled: boolean('payouts_enabled').default(false).notNull(),
    chargesEnabled: boolean('charges_enabled').default(false).notNull(),
    tax1099Ready: boolean('tax_1099_ready').default(false).notNull(),
    externalAccountReady: boolean('external_account_ready').default(false).notNull(),
    stripeAccountType: varchar('stripe_account_type').default('express').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_connected_accounts_creator_id').on(table.creatorId),
    index('idx_connected_accounts_stripe_account_id').on(table.stripeAccountId),
    uniqueIndex('uidx_connected_accounts_payee').on(table.payeeType, table.payeeId),
  ]
);

// Payout release job per class session payment
export const payoutReleaseJobs = pgTable(
  'payout_release_jobs',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    // Null for venue_owner jobs (venue owners are users, not creators).
    creatorId: varchar('creator_id').references(() => creators.id),
    // Generalized payee identity. payeeId = creatorId for creator jobs,
    // owner userId for venue_owner jobs.
    payeeType: varchar('payee_type').default('creator').notNull(), // creator | venue_owner
    payeeId: varchar('payee_id'),
    orderId: varchar('order_id')
      .notNull()
      .references(() => orders.id),
    connectedAccountId: varchar('connected_account_id').references(() => connectedAccounts.id),
    eligibleAt: timestamp('eligible_at').notNull(), // session_end_time + payout_delay_hours
    status: varchar('status').default('scheduled').notNull(),
    // scheduled | blocked | released | reversed | failed
    stripeTransferId: varchar('stripe_transfer_id'),
    stripePayoutId: varchar('stripe_payout_id'),
    holdReason: text('hold_reason'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    // Hourly job polls scheduled + eligible_at
    index('idx_payout_release_jobs_status_eligible').on(table.status, table.eligibleAt),
    index('idx_payout_release_jobs_creator_id').on(table.creatorId),
    index('idx_payout_release_jobs_order_id').on(table.orderId),
    // One job per payee type per order (creator + venue_owner) — makes the
    // snapshot's onConflictDoNothing inserts genuinely idempotent.
    uniqueIndex('uidx_payout_release_jobs_order_payee').on(table.orderId, table.payeeType),
  ]
);

// Reconciliation period headers
export const payoutBatches = pgTable('payout_batches', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  periodStart: timestamp('period_start').notNull(),
  periodEnd: timestamp('period_end').notNull(),
  status: varchar('status').default('draft').notNull(), // draft | finalized | exported | closed
  operatorNotes: text('operator_notes'),
  createdBy: varchar('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Per-creator row within a reconciliation period
export const payoutBatchItems = pgTable(
  'payout_batch_items',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    batchId: varchar('batch_id')
      .notNull()
      .references(() => payoutBatches.id),
    creatorId: varchar('creator_id')
      .notNull()
      .references(() => creators.id),
    grossEarnedCents: integer('gross_earned_cents').default(0).notNull(),
    refundsCents: integer('refunds_cents').default(0).notNull(),
    chargebacksCents: integer('chargebacks_cents').default(0).notNull(),
    netPayableCents: integer('net_payable_cents').default(0).notNull(),
    transferTotalCents: integer('transfer_total_cents').default(0).notNull(),
    reconciliationStatus: varchar('reconciliation_status').default('pending').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_payout_batch_items_batch_id').on(table.batchId),
    index('idx_payout_batch_items_creator_id').on(table.creatorId),
    uniqueIndex('uidx_payout_batch_items_batch_creator').on(table.batchId, table.creatorId),
  ]
);

// Stripe webhook idempotency guard
export const processedStripeEvents = pgTable('processed_stripe_events', {
  id: varchar('id')
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  stripeEventId: varchar('stripe_event_id').notNull().unique(),
  eventType: varchar('event_type').notNull(),
  processedAt: timestamp('processed_at').defaultNow().notNull(),
});

// ─────────────────────────────────────────────────────────────────────────────

// Email CMS — template management, versioning, assets, and suppression

export const emailTemplates = pgTable(
  'email_templates',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    slug: varchar('slug').notNull().unique(), // matches EmailTemplateKey
    name: varchar('name').notNull(),
    description: text('description'),
    category: varchar('category').notNull(), // account | enrollment | creator | financial | community | blog | calendar | system
    status: varchar('status').default('draft').notNull(), // draft | active | archived
    templateType: varchar('template_type').default('transactional').notNull(), // transactional | system | campaign
    preferenceKey: varchar('preference_key'), // null = cannot opt out
    bypassable: boolean('bypassable').default(false).notNull(),
    activeVersionId: varchar('active_version_id'), // FK set after first publish
    createdBy: varchar('created_by').references(() => users.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_email_templates_slug').on(table.slug),
    index('idx_email_templates_status').on(table.status),
    index('idx_email_templates_category').on(table.category),
  ]
);

export const emailTemplateVersions = pgTable(
  'email_template_versions',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    templateId: varchar('template_id')
      .notNull()
      .references(() => emailTemplates.id, { onDelete: 'cascade' }),
    versionNumber: integer('version_number').notNull(),
    stage: varchar('stage').default('draft').notNull(), // draft | published | archived
    sourceVersionId: varchar('source_version_id'), // which version this was branched from
    subject: varchar('subject').notNull(),
    previewText: text('preview_text'),
    fromName: varchar('from_name'),
    replyToEmail: varchar('reply_to_email'),
    grapesjsProject: jsonb('grapesjs_project')
      .$type<Record<string, unknown>>()
      .default(sql`'{}'::jsonb`)
      .notNull(),
    mjmlMarkup: text('mjml_markup').default('').notNull(),
    htmlMarkup: text('html_markup').default('').notNull(), // compiled from MJML on save
    textMarkup: text('text_markup').default('').notNull(), // plain-text version
    createdBy: varchar('created_by').references(() => users.id),
    publishedAt: timestamp('published_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => [
    index('idx_email_template_versions_template_id').on(table.templateId),
    index('idx_email_template_versions_stage').on(table.templateId, table.stage),
    uniqueIndex('uidx_email_template_versions_number').on(table.templateId, table.versionNumber),
  ]
);

export const emailAssets = pgTable(
  'email_assets',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    filename: varchar('filename').notNull(), // slugified, used as GCS object name component
    originalFilename: varchar('original_filename'),
    contentType: varchar('content_type').notNull(), // image/jpeg | image/png | image/webp | image/gif
    byteSize: integer('byte_size').notNull(),
    width: integer('width'),
    height: integer('height'),
    altText: text('alt_text'),
    storageUrl: text('storage_url').notNull(), // public GCS URL — stable, permanent
    createdBy: varchar('created_by').references(() => users.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [index('idx_email_assets_created_at').on(table.createdAt)]
);

export const emailSuppressionList = pgTable(
  'email_suppression_list',
  {
    id: varchar('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    emailNormalized: varchar('email_normalized').notNull().unique(), // lowercase trimmed
    reason: varchar('reason').notNull(), // unsubscribe_link | bounce | complaint | manual | account_deleted
    source: varchar('source').notNull(), // user_action | admin | system
    suppressedAt: timestamp('suppressed_at').defaultNow().notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => [
    index('idx_email_suppression_email').on(table.emailNormalized),
    index('idx_email_suppression_reason').on(table.reason),
    index('idx_email_suppression_suppressed_at').on(table.suppressedAt),
  ]
);

// ─────────────────────────────────────────────────────────────────────────────

// Relations
export const userRelations = relations(users, ({ one, many }) => ({
  creatorProfile: one(creators, { fields: [users.id], references: [creators.userId] }),
  enrollments: many(enrollments),
  tips: many(tips),
  communityTopics: many(communityTopics),
  communityReplies: many(communityReplies),
}));

export const stateRelations = relations(states, ({ many }) => ({
  regions: many(regions),
}));

export const regionRelations = relations(regions, ({ one, many }) => ({
  state: one(states, { fields: [regions.stateId], references: [states.id] }),
  localities: many(localities),
  classes: many(classes),
  calendarEvents: many(calendarEvents),
  communityTopics: many(communityTopics),
}));

export const classLocationRelations = relations(classLocations, ({ one, many }) => ({
  locality: one(localities, { fields: [classLocations.localityId], references: [localities.id] }),
  classes: many(classes),
}));

export const localityRelations = relations(localities, ({ one, many }) => ({
  region: one(regions, { fields: [localities.regionId], references: [regions.id] }),
  creators: many(creators),
  classes: many(classes),
  calendarEvents: many(calendarEvents),
  communityTopics: many(communityTopics),
  classLocations: many(classLocations),
}));

export const creatorRelations = relations(creators, ({ one, many }) => ({
  user: one(users, { fields: [creators.userId], references: [users.id] }),
  locality: one(localities, { fields: [creators.localityId], references: [localities.id] }),
  classes: many(classes),
  tips: many(tips),
  featuredLogos: many(featuredLogos),
  connectedAccount: one(connectedAccounts, {
    fields: [creators.id],
    references: [connectedAccounts.creatorId],
  }),
  ledgerEntries: many(creatorLedgerEntries),
  payoutReleaseJobs: many(payoutReleaseJobs),
  students: many(creatorStudents),
}));

export const classRelations = relations(classes, ({ one, many }) => ({
  creator: one(creators, { fields: [classes.creatorId], references: [creators.id] }),
  category: one(categories, { fields: [classes.categoryId], references: [categories.id] }),
  locality: one(localities, { fields: [classes.localityId], references: [localities.id] }),
  venue: one(classLocations, { fields: [classes.venueId], references: [classLocations.id] }),
  enrollments: many(enrollments),
  waiverSignatures: many(classWaiverSignatures),
  communityTopics: many(communityTopics),
  sessions: many(classSessions),
  refundRequests: many(refundRequests),
  userPasses: many(userPasses),
}));

export const classSessionRelations = relations(classSessions, ({ one, many }) => ({
  class: one(classes, { fields: [classSessions.classId], references: [classes.id] }),
  enrollments: many(enrollments),
  waiverSignatures: many(classWaiverSignatures),
  refundRequests: many(refundRequests),
  passRedemptions: many(passRedemptions),
}));

export const enrollmentRelations = relations(enrollments, ({ one, many }) => ({
  user: one(users, { fields: [enrollments.userId], references: [users.id] }),
  class: one(classes, { fields: [enrollments.classId], references: [classes.id] }),
  session: one(classSessions, { fields: [enrollments.sessionId], references: [classSessions.id] }),
  order: one(orders, { fields: [enrollments.orderId], references: [orders.id] }),
  attendees: many(enrollmentAttendees),
  waiverSignatures: many(classWaiverSignatures),
}));

export const checkoutSessionRelations = relations(checkoutSessions, ({ one }) => ({
  user: one(users, { fields: [checkoutSessions.userId], references: [users.id] }),
  class: one(classes, { fields: [checkoutSessions.classId], references: [classes.id] }),
  session: one(classSessions, {
    fields: [checkoutSessions.sessionId],
    references: [classSessions.id],
  }),
}));

export const orderRelations = relations(orders, ({ one, many }) => ({
  user: one(users, { fields: [orders.userId], references: [users.id] }),
  class: one(classes, { fields: [orders.classId], references: [classes.id] }),
  session: one(classSessions, { fields: [orders.sessionId], references: [classSessions.id] }),
  checkoutSession: one(checkoutSessions, {
    fields: [orders.checkoutSessionId],
    references: [checkoutSessions.id],
  }),
  attendees: many(enrollmentAttendees),
  refundRequests: many(refundRequests),
}));

export const enrollmentAttendeeRelations = relations(enrollmentAttendees, ({ one }) => ({
  enrollment: one(enrollments, {
    fields: [enrollmentAttendees.enrollmentId],
    references: [enrollments.id],
  }),
  order: one(orders, { fields: [enrollmentAttendees.orderId], references: [orders.id] }),
  user: one(users, { fields: [enrollmentAttendees.userId], references: [users.id] }),
  waiverSignature: one(classWaiverSignatures, {
    fields: [enrollmentAttendees.id],
    references: [classWaiverSignatures.attendeeId],
  }),
}));

export const classWaiverSignatureRelations = relations(classWaiverSignatures, ({ one }) => ({
  class: one(classes, { fields: [classWaiverSignatures.classId], references: [classes.id] }),
  session: one(classSessions, {
    fields: [classWaiverSignatures.sessionId],
    references: [classSessions.id],
  }),
  enrollment: one(enrollments, {
    fields: [classWaiverSignatures.enrollmentId],
    references: [enrollments.id],
  }),
  attendee: one(enrollmentAttendees, {
    fields: [classWaiverSignatures.attendeeId],
    references: [enrollmentAttendees.id],
  }),
  user: one(users, { fields: [classWaiverSignatures.userId], references: [users.id] }),
}));

export const creatorStudentRelations = relations(creatorStudents, ({ one }) => ({
  creator: one(creators, { fields: [creatorStudents.creatorId], references: [creators.id] }),
}));

export const refundRequestRelations = relations(refundRequests, ({ one }) => ({
  order: one(orders, { fields: [refundRequests.orderId], references: [orders.id] }),
  user: one(users, { fields: [refundRequests.userId], references: [users.id] }),
  class: one(classes, { fields: [refundRequests.classId], references: [classes.id] }),
  session: one(classSessions, {
    fields: [refundRequests.sessionId],
    references: [classSessions.id],
  }),
  creator: one(creators, { fields: [refundRequests.creatorId], references: [creators.id] }),
  reviewer: one(users, { fields: [refundRequests.reviewedBy], references: [users.id] }),
}));

export const tipRelations = relations(tips, ({ one }) => ({
  user: one(users, { fields: [tips.userId], references: [users.id] }),
  creator: one(creators, { fields: [tips.creatorId], references: [creators.id] }),
}));

export const featuredLogoRelations = relations(featuredLogos, ({ one }) => ({
  creator: one(creators, { fields: [featuredLogos.creatorId], references: [creators.id] }),
}));

export const communityTopicRelations = relations(communityTopics, ({ one, many }) => ({
  user: one(users, { fields: [communityTopics.userId], references: [users.id] }),
  region: one(regions, { fields: [communityTopics.regionId], references: [regions.id] }),
  locality: one(localities, {
    fields: [communityTopics.localityId],
    references: [localities.id],
  }),
  class: one(classes, { fields: [communityTopics.classId], references: [classes.id] }),
  replies: many(communityReplies),
}));

export const communityReplyRelations = relations(communityReplies, ({ one }) => ({
  topic: one(communityTopics, {
    fields: [communityReplies.topicId],
    references: [communityTopics.id],
  }),
  user: one(users, { fields: [communityReplies.userId], references: [users.id] }),
}));

export const communityLikeRelations = relations(communityLikes, ({ one }) => ({
  user: one(users, { fields: [communityLikes.userId], references: [users.id] }),
  topic: one(communityTopics, {
    fields: [communityLikes.topicId],
    references: [communityTopics.id],
  }),
  reply: one(communityReplies, {
    fields: [communityLikes.replyId],
    references: [communityReplies.id],
  }),
}));

export const classReviewRelations = relations(classReviews, ({ one }) => ({
  user: one(users, { fields: [classReviews.userId], references: [users.id] }),
  class: one(classes, { fields: [classReviews.classId], references: [classes.id] }),
  order: one(orders, { fields: [classReviews.orderId], references: [orders.id] }),
}));

export const userPassRelations = relations(userPasses, ({ one, many }) => ({
  user: one(users, { fields: [userPasses.userId], references: [users.id] }),
  class: one(classes, { fields: [userPasses.classId], references: [classes.id] }),
  order: one(orders, { fields: [userPasses.orderId], references: [orders.id] }),
  redemptions: many(passRedemptions),
}));

export const passRedemptionRelations = relations(passRedemptions, ({ one }) => ({
  userPass: one(userPasses, { fields: [passRedemptions.userPassId], references: [userPasses.id] }),
  session: one(classSessions, {
    fields: [passRedemptions.classSessionId],
    references: [classSessions.id],
  }),
  enrollment: one(enrollments, {
    fields: [passRedemptions.enrollmentId],
    references: [enrollments.id],
  }),
}));

export const passwordResetTokenRelations = relations(passwordResetTokens, ({ one }) => ({
  user: one(users, { fields: [passwordResetTokens.userId], references: [users.id] }),
}));

export const emailVerificationTokenRelations = relations(emailVerificationTokens, ({ one }) => ({
  user: one(users, { fields: [emailVerificationTokens.userId], references: [users.id] }),
}));

export const blogPostRelations = relations(blogPosts, ({ one }) => ({
  author: one(users, { fields: [blogPosts.authorId], references: [users.id] }),
  category: one(blogCategories, {
    fields: [blogPosts.categoryId],
    references: [blogCategories.id],
  }),
  region: one(regions, { fields: [blogPosts.regionId], references: [regions.id] }),
  locality: one(localities, { fields: [blogPosts.localityId], references: [localities.id] }),
  reviewer: one(users, { fields: [blogPosts.reviewedBy], references: [users.id] }),
}));

export const blogSubscriptionRelations = relations(blogSubscriptions, ({ one }) => ({
  region: one(regions, { fields: [blogSubscriptions.regionId], references: [regions.id] }),
}));

// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCreatorSchema = createInsertSchema(creators).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertClassSchema = createInsertSchema(classes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertClassSessionSchema = createInsertSchema(classSessions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertEnrollmentSchema = createInsertSchema(enrollments).omit({
  id: true,
  enrolledAt: true,
});
export const insertTipSchema = createInsertSchema(tips).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertFeaturedLogoSchema = createInsertSchema(featuredLogos).omit({
  id: true,
  createdAt: true,
});
export const insertCommunityTopicSchema = createInsertSchema(communityTopics).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCommunityReplySchema = createInsertSchema(communityReplies).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertNewsletterSchema = createInsertSchema(newsletters).omit({
  id: true,
  subscribedAt: true,
});
export const insertPartnerApplicationSchema = createInsertSchema(partnerApplications).omit({
  id: true,
  createdAt: true,
});
export const insertPageContentSchema = createInsertSchema(pageContent).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({
  id: true,
  createdAt: true,
});
export const insertEmailVerificationTokenSchema = createInsertSchema(emailVerificationTokens).omit({
  id: true,
  createdAt: true,
});
export const insertOutboundEmailSchema = createInsertSchema(outboundEmails).omit({
  id: true,
  queuedAt: true,
  lastAttemptAt: true,
  sentAt: true,
  nextRetryAt: true,
  updatedAt: true,
});
export const insertEmailUsageTelemetrySchema = createInsertSchema(emailUsageTelemetry).omit({
  id: true,
  createdAt: true,
});
export const insertPromoCodeSchema = createInsertSchema(promoCodes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCheckoutSessionSchema = createInsertSchema(checkoutSessions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertOrderSchema = createInsertSchema(orders).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertEnrollmentAttendeeSchema = createInsertSchema(enrollmentAttendees).omit({
  id: true,
  createdAt: true,
});
export const insertClassWaiverSignatureSchema = createInsertSchema(classWaiverSignatures).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCreatorStudentSchema = createInsertSchema(creatorStudents).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertRefundRequestSchema = createInsertSchema(refundRequests).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertCommunityLikeSchema = createInsertSchema(communityLikes).omit({
  id: true,
  createdAt: true,
});
export const insertClassReviewSchema = createInsertSchema(classReviews).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertClassLocationSchema = createInsertSchema(classLocations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertUserPassSchema = createInsertSchema(userPasses).omit({
  id: true,
  createdAt: true,
});
export const insertPassRedemptionSchema = createInsertSchema(passRedemptions).omit({
  id: true,
  redeemedAt: true,
});

export const insertBlogCategorySchema = createInsertSchema(blogCategories).omit({
  id: true,
  createdAt: true,
});
export const insertBlogPostSchema = createInsertSchema(blogPosts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertBlogSubscriptionSchema = createInsertSchema(blogSubscriptions).omit({
  id: true,
  subscribedAt: true,
});

// Types
export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;
export type Creator = typeof creators.$inferSelect;
export type InsertCreator = z.infer<typeof insertCreatorSchema>;
export type Class = typeof classes.$inferSelect;
export type InsertClass = z.infer<typeof insertClassSchema>;
export type ClassSession = typeof classSessions.$inferSelect;
export type InsertClassSession = z.infer<typeof insertClassSessionSchema>;
export type Category = typeof categories.$inferSelect;
export type Enrollment = typeof enrollments.$inferSelect;
export type InsertEnrollment = z.infer<typeof insertEnrollmentSchema>;
export type Tip = typeof tips.$inferSelect;
export type InsertTip = z.infer<typeof insertTipSchema>;
export type FeaturedLogo = typeof featuredLogos.$inferSelect;
export type InsertFeaturedLogo = z.infer<typeof insertFeaturedLogoSchema>;
export type Newsletter = typeof newsletters.$inferSelect;
export type InsertNewsletter = z.infer<typeof insertNewsletterSchema>;
export type PartnerApplication = typeof partnerApplications.$inferSelect;
export type InsertPartnerApplication = z.infer<typeof insertPartnerApplicationSchema>;
export type PageContent = typeof pageContent.$inferSelect;
export type InsertPageContent = z.infer<typeof insertPageContentSchema>;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;
export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;
export type EmailVerificationToken = typeof emailVerificationTokens.$inferSelect;
export type InsertEmailVerificationToken = z.infer<typeof insertEmailVerificationTokenSchema>;
export type OutboundEmail = typeof outboundEmails.$inferSelect;
export type InsertOutboundEmail = z.infer<typeof insertOutboundEmailSchema>;
export type EmailUsageTelemetry = typeof emailUsageTelemetry.$inferSelect;
export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type EmailTemplateVersion = typeof emailTemplateVersions.$inferSelect;
export type EmailAsset = typeof emailAssets.$inferSelect;
export type EmailSuppressionEntry = typeof emailSuppressionList.$inferSelect;
export type InsertEmailUsageTelemetry = z.infer<typeof insertEmailUsageTelemetrySchema>;
export type PromoCode = typeof promoCodes.$inferSelect;
export type InsertPromoCode = z.infer<typeof insertPromoCodeSchema>;
export type CheckoutSession = typeof checkoutSessions.$inferSelect;
export type InsertCheckoutSession = z.infer<typeof insertCheckoutSessionSchema>;
export type Order = typeof orders.$inferSelect;
export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type EnrollmentAttendee = typeof enrollmentAttendees.$inferSelect;
export type InsertEnrollmentAttendee = z.infer<typeof insertEnrollmentAttendeeSchema>;
export type ClassWaiverSignature = typeof classWaiverSignatures.$inferSelect;
export type InsertClassWaiverSignature = z.infer<typeof insertClassWaiverSignatureSchema>;
export type CreatorStudent = typeof creatorStudents.$inferSelect;
export type InsertCreatorStudent = z.infer<typeof insertCreatorStudentSchema>;
export type RefundRequest = typeof refundRequests.$inferSelect;
export type InsertRefundRequest = z.infer<typeof insertRefundRequestSchema>;

// Phase 2 geography types
export type State = typeof states.$inferSelect;
export type InsertState = typeof states.$inferInsert;
export type Region = typeof regions.$inferSelect;
export type InsertRegion = typeof regions.$inferInsert;
export type Locality = typeof localities.$inferSelect;
export type InsertLocality = typeof localities.$inferInsert;
export type CalendarEvent = typeof calendarEvents.$inferSelect;
export type InsertCalendarEvent = typeof calendarEvents.$inferInsert;

export const adminCreateCalendarEventSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().nullable().optional(),
  regionId: z.string().nullable().optional(),
  localityId: z.string().nullable().optional(),
  venueId: z.string().nullable().optional(),
  category: z.enum(CALENDAR_CATEGORIES).default('community'),
  attendanceMode: z.enum(['in_person', 'online', 'hybrid']).default('in_person'),
  eventStatus: z.enum(['scheduled', 'cancelled', 'postponed']).default('scheduled'),
  externalUrl: z.string().url().nullable().optional(),
  coverImageUrl: z.string().url().nullable().optional(),
  isPublished: z.boolean().default(false),
  isFeatured: z.boolean().default(false),
  featuredOrder: z.number().int().nullable().optional(),
  tags: z.array(z.string()).default([]),
});

export const adminUpdateCalendarEventSchema = adminCreateCalendarEventSchema.partial();

export type AdminCreateCalendarEventInput = z.infer<typeof adminCreateCalendarEventSchema>;
export type AdminUpdateCalendarEventInput = z.infer<typeof adminUpdateCalendarEventSchema>;

// Venue owners self-manage calendar items for their own space. Unlike the admin
// schema: no isFeatured/featuredOrder (admin-only), no isPublished (server-forces
// true — auto-published, no moderation queue), no regionId/localityId (server
// derives both from the venue's own locality rather than trusting client input).
export const venueCreateCalendarEventSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().nullable().optional(),
  venueId: z.string().min(1),
  category: z.enum(CALENDAR_CATEGORIES).default('community'),
  attendanceMode: z.enum(['in_person', 'online', 'hybrid']).default('in_person'),
  eventStatus: z.enum(['scheduled', 'cancelled', 'postponed']).default('scheduled'),
  externalUrl: z.string().url().nullable().optional(),
  coverImageUrl: z.string().url().nullable().optional(),
  tags: z.array(z.string()).default([]),
});

export const venueUpdateCalendarEventSchema = venueCreateCalendarEventSchema.partial();

export type VenueCreateCalendarEventInput = z.infer<typeof venueCreateCalendarEventSchema>;
export type VenueUpdateCalendarEventInput = z.infer<typeof venueUpdateCalendarEventSchema>;

export const adminCreateSeriesSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  category: z.enum(CALENDAR_CATEGORIES).default('community'),
  tags: z.array(z.string()).default([]),
  venueId: z.string().nullable().optional(),
  externalUrl: z.string().url().nullable().optional(),
  coverImageUrl: z.string().url().nullable().optional(),
  rrule: z.string().min(1).max(500),
  dtstart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/, 'Must be local ISO datetime (no timezone)'),
  timezone: z.string().default('America/Indiana/Indianapolis'),
  durationMinutes: z.number().int().min(1).max(1440).default(60),
  regionId: z.string().nullable().optional(),
  localityId: z.string().nullable().optional(),
  status: z.enum(['draft', 'preview', 'published', 'archived']).default('draft'),
});
export const adminUpdateSeriesSchema = adminCreateSeriesSchema.partial();
export type AdminCreateSeriesInput = z.infer<typeof adminCreateSeriesSchema>;
export type AdminUpdateSeriesInput = z.infer<typeof adminUpdateSeriesSchema>;

export const adminCreateFeedSchema = z.object({
  name: z.string().min(1).max(200),
  icalUrl: z.string().url(),
  regionId: z.string().nullable().optional(),
  localityId: z.string().nullable().optional(),
  defaultCategory: z.enum(CALENDAR_CATEGORIES).default('community'),
  defaultVenueId: z.string().nullable().optional(),
  sourceName: z.string().max(150).nullable().optional(),
  isActive: z.boolean().default(true),
});
export const adminUpdateFeedSchema = adminCreateFeedSchema.partial();
export type AdminCreateFeedInput = z.infer<typeof adminCreateFeedSchema>;
export type AdminUpdateFeedInput = z.infer<typeof adminUpdateFeedSchema>;
export type CommunityTopic = typeof communityTopics.$inferSelect;
export type InsertCommunityTopic = z.infer<typeof insertCommunityTopicSchema>;
export type CommunityReply = typeof communityReplies.$inferSelect;
export type InsertCommunityReply = z.infer<typeof insertCommunityReplySchema>;
export type CommunityReport = typeof communityReports.$inferSelect;
export type CommunityLike = typeof communityLikes.$inferSelect;
export type InsertCommunityLike = z.infer<typeof insertCommunityLikeSchema>;
export type ClassReview = typeof classReviews.$inferSelect;
export type InsertClassReview = z.infer<typeof insertClassReviewSchema>;
export type ClassLocation = typeof classLocations.$inferSelect;
export type InsertClassLocation = z.infer<typeof insertClassLocationSchema>;
export type PassProduct = typeof passProducts.$inferSelect;
export type PassProductClass = typeof passProductClasses.$inferSelect;
export type UserPass = typeof userPasses.$inferSelect;
export type InsertUserPass = z.infer<typeof insertUserPassSchema>;
export type PassRedemption = typeof passRedemptions.$inferSelect;
export type PassAdjustment = typeof passAdjustments.$inferSelect;
export type PassTransfer = typeof passTransfers.$inferSelect;
export type InsertPassRedemption = z.infer<typeof insertPassRedemptionSchema>;

// Blog types
export type BlogCategory = typeof blogCategories.$inferSelect;
export type InsertBlogCategory = z.infer<typeof insertBlogCategorySchema>;
export type BlogPost = typeof blogPosts.$inferSelect;
export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;
export type BlogSubscription = typeof blogSubscriptions.$inferSelect;
export type InsertBlogSubscription = z.infer<typeof insertBlogSubscriptionSchema>;

// Phase 1B types
export type FeeProfile = typeof feeProfiles.$inferSelect;
export type OrderFinancials = typeof orderFinancials.$inferSelect;
export type CreatorLedgerEntry = typeof creatorLedgerEntries.$inferSelect;
export type ConnectedAccount = typeof connectedAccounts.$inferSelect;
export type PayoutReleaseJob = typeof payoutReleaseJobs.$inferSelect;
export type PayoutBatch = typeof payoutBatches.$inferSelect;
export type PayoutBatchItem = typeof payoutBatchItems.$inferSelect;
export type ProcessedStripeEvent = typeof processedStripeEvents.$inferSelect;
